// @flow

import * as React from 'react';
import Relay from 'react-relay/classic';

import CableStore from 'app/stores/CableStore';

import { elementIdFor } from 'app/components/job/Job';
import ShowMoreFooter from 'app/components/shared/ShowMoreFooter';
import Annotation, { type AnnotationStyles } from './Annotation';

import LegacyDispatcher from 'app/lib/legacyDispatcher';

const PAGE_SIZE = 10;

type Props = {
  build: {
    id: string,
    annotations: {
      edges: Array<{
        node: {
          id: string,
          context: string,
          style: AnnotationStyles,
          body: {
            html: ?string
          }
        }
      }>
    }
  },
  relay: Object
};

type State = {
  loadingMore: boolean
};

class AnnnotationsList extends React.PureComponent<Props, State> {
  state = {
    loadingMore: false
  };

  componentDidMount() {
    CableStore.on("build:annotations_change", this.handleWebsocketEvent);
  }

  componentWillUnmount() {
    CableStore.off("build:annotations_change", this.handleWebsocketEvent);
  }

  render() {
    const annotations = this.props.build.annotations.edges.map((edge) => {
      return <Annotation key={edge.node.id} annotation={edge.node} />;
    });

    return (
      <div onClick={this.handleAnnotationClick}>
        {annotations}
        {this.renderShowMore()}
      </div>
    );
  }

  renderShowMore() {
    if (this.props.build.annotations) {
      return (
        <div className="mb4">
          <ShowMoreFooter
            connection={this.props.build.annotations}
            loading={this.state.loadingMore}
            onShowMore={this.handleShowMoreAnnotations}
            label="annotations"
          />
        </div>
      );
    }
  }

  handleShowMoreAnnotations = () => {
    this.setState({ loadingMore: true });

    this.props.relay.setVariables(
      {
        pageSize: this.props.relay.variables.pageSize + PAGE_SIZE
      },
      (readyState) => {
        if (readyState.done) {
          this.setState({ loadingMore: false });
        }
      }
    );
  };

  handleAnnotationClick = (event: SyntheticMouseEvent<HTMLDivElement>) => {
    // Don't change anything if the user is using any modifier keys
    if ((event.button !== 0) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    // Don't change clicks in things which aren't <a /> elements, or don't have a `href`
    if (!(event.target instanceof HTMLAnchorElement) || !event.target.href) {
      return;
    }

    // Parse the link URL, relative to the current location
    const linkUrl = new URL(event.target.href, window.location.href);

    // Don't do anything to links which go somewhere other than this current page
    if (`${linkUrl.origin}${linkUrl.pathname}` !== `${window.location.origin}${window.location.pathname}`) {
      return;
    }

    const jobId = linkUrl.hash.slice(1);

    // If we can't find a job component for that job ID, don't do anything
    if (!document.getElementById(elementIdFor(jobId))) {
      return;
    }

    // Finally, if we now know this is a valid URL and job ID,
    // cancel the browser's usual navigation, and emit a "job:focus" event
    event.preventDefault();

    // NOTE: You're supposed to pass a "full" job object here, but
    // only actually consumes "id" and "path" - so here we fake it!
    LegacyDispatcher.emit(
      "job:focus",
      {
        job: {
          id: jobId,
          path: ((event.target: any): HTMLAnchorElement).href
        }
      }
    );
  }

  handleWebsocketEvent = (payload) => {
    if (payload.buildID === this.props.build.id) {
      this.props.relay.forceFetch();
    }
  };
}

export default Relay.createContainer(AnnnotationsList, {
  initialVariables: {
    pageSize: PAGE_SIZE
  },

  fragments: {
    build: () => Relay.QL`
      fragment on Build {
        id
        annotations(first: $pageSize) {
          edges {
            node {
              id
              context
              style
              body {
                html
              }
            }
            cursor
          }
          ${ShowMoreFooter.getFragment('connection')}
        }
      }
    `
  }
});
